import { Container, Graphics } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { ECONOMY } from '@/config/economy';
import { MAX_ACTIVE_WEAPONS } from '@/config/weapons';
import { t } from '@/i18n';
import { bagGlyph } from './hangar-icons';
import { buildCompare, compareEquip, socketPower } from './shop-compare';
import {
  BAG_TILE, SOCKET_TILE,
  buildBagBuyTile, buildBagTile, buildCrateTile, buildEmptyBagTile, buildSocketTile,
} from './shop-hangar-tiles';
import { bagWeapons, type FocusItem, type ShopState } from './shop-types';
import { chamferPanel, label } from './widgets';

/**
 * Hangar v4 (docs/09 wave 4): LEFT — the ship's sockets 1-2-3 as big
 * numbered tiles (category outline, live power) with the comparison strip
 * under them; RIGHT — the BAG of spare weapons (glyph, ▲/▼ vs its target
 * socket, "new" sparkle) + its padlock, and the swap-or-scrap crate when
 * the bag is full. Picked-up ≠ equipped: equipping is one action here.
 */
export const HANGAR_W = 970;
export const HANGAR_H = 292;
const SOCKET_X0 = 18;
const SOCKET_Y = 36;
const SOCKET_GAP = 10;
const COMPARE_Y = SOCKET_Y + SOCKET_TILE.h + 10;
const BAG_X0 = 432;
const BAG_Y0 = 36;
const BAG_GAP = 8;
const BAG_COLS = 3;
const CRATE_X = 782;

/** Socket tile i, hangar-local top-left. */
export function socketTilePos(i: number): { x: number; y: number } {
  return { x: SOCKET_X0 + i * (SOCKET_TILE.w + SOCKET_GAP), y: SOCKET_Y };
}

/** Bag cell i (weapons, then empty slots, then the padlock), hangar-local. */
export function bagTilePos(i: number): { x: number; y: number } {
  const col = i % BAG_COLS;
  const row = Math.floor(i / BAG_COLS);
  return { x: BAG_X0 + col * (BAG_TILE.w + BAG_GAP), y: BAG_Y0 + row * (BAG_TILE.h + BAG_GAP) };
}

export const CRATE_POS = { x: CRATE_X, y: BAG_Y0 } as const;

/** Bag cell of a focus item (weapon / padlock), -1 when not in the grid. */
export function bagCellOf(item: FocusItem, s: ShopState): number {
  if (item.kind === 'bagBuy') return Math.min(s.bagCap, ECONOMY.inventory.total - 1);
  if (item.kind !== 'weapon') return -1;
  return bagWeapons(s).indexOf(item.weaponId);
}

export interface HangarHooks {
  isSel(item: FocusItem): boolean;
  interactive(node: Container, item: FocusItem): void;
  /** Socket a bag weapon / the crate would go into right now */
  targetFor(weaponId: string): number;
  /** Yellow-lit socket (the focused weapon's target or the pinned one), -1 = none */
  targetSocket: number;
  /** Bag weapon whose scrap is armed (next press confirms) */
  armed: string | null;
  /** Touch: every bag tile shows its ✕ badge */
  scrapBadges: boolean;
  scrap(weaponId: string): void;
  swap(weaponId: string): void;
  /** Comparison strip subject */
  compare: { weaponId: string; socket: number } | { socket: number } | null;
}

export function buildHangar(s: ShopState, hooks: HangarHooks): Container {
  const c = new Container();
  const panel = new Graphics();
  chamferPanel(panel, HANGAR_W, HANGAR_H, { fill: 0x110e1b, stroke: 0x2a2440, notch: 16 });
  c.addChild(panel);
  const ship = label(t('shop.sockets'), 12, COLORS.uiAccent, 3);
  ship.position.set(SOCKET_X0, 12);
  c.addChild(ship);

  // ---- Sockets 1-2-3
  for (let i = 0; i < MAX_ACTIVE_WEAPONS; i++) {
    const item: FocusItem = { kind: 'socket', index: i };
    const open = i < s.equipCapacity;
    const weaponId = open ? s.equipped[i] ?? null : null;
    const price = !open && i === s.equipCapacity ? s.socketPrice : null;
    const tile = buildSocketTile({
      index: i, weaponId, open, price,
      affordable: price !== null && s.red >= price,
      power: weaponId ? socketPower(s, i) : 0,
      selected: hooks.isSel(item),
      target: hooks.targetSocket === i,
      fresh: weaponId !== null && s.fresh.includes(weaponId),
    });
    const p = socketTilePos(i);
    tile.position.set(p.x, p.y);
    if (open || price !== null) hooks.interactive(tile, item);
    c.addChild(tile);
  }
  const compare = buildCompare(s, hooks.compare);
  compare.position.set(SOCKET_X0, COMPARE_Y);
  c.addChild(compare);

  // ---- Bag header: glyph + count (orange when full)
  const bag = bagWeapons(s);
  const full = bag.length >= s.bagCap;
  const head = new Graphics();
  bagGlyph(head, BAG_X0 + 12, 22, 10, full ? COLORS.dangerOrange : COLORS.playerCream);
  c.addChild(head);
  const count = label(`${bag.length}/${s.bagCap}`, 16, full ? COLORS.dangerOrange : 0xe8eaf0, 1);
  count.anchor.set(0, 0.5);
  count.position.set(BAG_X0 + 30, 22);
  const bagName = label(t('shop.bag'), 12, 0x8a9099, 3);
  bagName.anchor.set(0, 0.5);
  bagName.position.set(count.x + count.width + 10, 23);
  c.addChild(count, bagName);

  // ---- Bag cells: weapons, empty slots, the padlock
  const cells = Math.min(ECONOMY.inventory.total, s.bagCap + (s.bagPrice !== null ? 1 : 0));
  for (let i = 0; i < cells; i++) {
    const id = bag[i];
    let tile: Container;
    if (id) {
      const item: FocusItem = { kind: 'weapon', weaponId: id };
      const sel = hooks.isSel(item);
      tile = buildBagTile({
        weaponId: id, selected: sel, fresh: s.fresh.includes(id),
        delta: deltaFor(s, id, hooks.targetFor(id)),
        showScrap: hooks.scrapBadges || sel, armed: hooks.armed === id,
      }, () => hooks.scrap(id));
      hooks.interactive(tile, item);
    } else if (i < s.bagCap) {
      tile = buildEmptyBagTile();
    } else {
      const item: FocusItem = { kind: 'bagBuy' };
      tile = buildBagBuyTile(s.bagPrice!, s.yellow >= s.bagPrice!, hooks.isSel(item));
      hooks.interactive(tile, item);
    }
    const p = bagTilePos(i);
    tile.position.set(p.x, p.y);
    c.addChild(tile);
  }

  // ---- Swap-or-scrap crate (bag full when it was picked up)
  if (s.pending) {
    const id = s.pending;
    const item: FocusItem = { kind: 'weapon', weaponId: id };
    const tile = buildCrateTile(
      id, hooks.targetFor(id), hooks.isSel(item), () => hooks.swap(id), () => hooks.scrap(id),
    );
    tile.position.set(CRATE_POS.x, CRATE_POS.y);
    hooks.interactive(tile, item);
    c.addChild(tile);
  }
  return c;
}

/** Ship power change of equipping `id` into `socket`. */
function deltaFor(s: ShopState, id: string, socket: number): number {
  const { before, after } = compareEquip(s, id, socket);
  return after.power - before.power;
}
